/**
 * Rebuy Widget Customizations
 * 
 * - Disables SmartCart (using theme's cart drawer instead)
 * - Refreshes cart drawer when products are added via Rebuy
 * - Re-initializes Rebuy widgets on cart changes (for empty cart state)
 * - Removes Rebuy's default CSS (custom styles in base.css)
 */

(function() {
  if (window.RebuyCustomizerLoaded) return;
  window.RebuyCustomizerLoaded = true;

  // Disable Rebuy SmartCart - we use the theme's cart drawer.
  // This script is deferred, so on some first loads Rebuy's CDN script fires
  // rebuy:smartcart.ready BEFORE our listener attaches — SmartCart then stayed alive
  // (with its CSS stripped below) and swallowed add-button clicks until a reload.
  // Destroy on the event AND immediately, whichever order the scripts land in.
  function destroySmartCart() {
    if (typeof Rebuy !== 'undefined' && Rebuy.SmartCart) {
      try {
        Rebuy.SmartCart.destroy();
      } catch (e) {
        /* already destroyed / not initialized yet */
      }
    }
  }
  document.addEventListener('rebuy:smartcart.ready', destroySmartCart);
  destroySmartCart();

  // Re-init ONLY when the cart drawer's widget container is empty (first render, or
  // a drawer morph wiped it — the server HTML has an empty div). Scoped to containers
  // INSIDE cart-items-component on purpose: standalone Rebuy divs elsewhere (e.g. the
  // Gift with Purchase logic widget in theme.liquid, data-rebuy-id="301275") render no
  // visible content and stay empty forever — treating them as "needs init" re-inits on
  // every cart update, and with a GWP gift in the cart each init fires Rebuy's own
  // cart-change event → infinite loop, cart widget reshuffles endlessly.
  // Logic-only widgets (no visible render): their containers are empty forever, so they
  // must NEVER count as "needs init" — even if a theme pull drops one back inside the drawer.
  const REBUY_LOGIC_WIDGET_IDS = ['301275'];

  function reinitRebuy() {
    if (typeof Rebuy === 'undefined') return;

    const containers = document.querySelectorAll('cart-items-component [data-rebuy-id]');
    if (!containers.length) return;

    for (const container of containers) {
      if (REBUY_LOGIC_WIDGET_IDS.includes(container.dataset.rebuyId)) continue;
      if (container.childElementCount === 0) {
        Rebuy.init();
        return;
      }
    }
  }

  // Re-init Rebuy on cart changes (handles empty cart state)
  document.addEventListener('rebuy:cart.change', () => {
    setTimeout(reinitRebuy, 100);
  });

  // Re-init Rebuy on theme cart updates (catches section re-renders)
  document.addEventListener('cart:update', () => {
    setTimeout(reinitRebuy, 200);
  });

  // Watch for Rebuy widget containers added to DOM (for empty cart state)
  const widgetObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1 && (node.matches?.('[data-rebuy-id]') || node.querySelector?.('[data-rebuy-id]'))) {
          setTimeout(reinitRebuy, 50);
          return;
        }
      }
    }
  });
  widgetObserver.observe(document.body, { childList: true, subtree: true });

  // Intercept fetch to refresh the cart ONLY for Rebuy's own adds.
  // The theme's native ATC, the custom-subscription block, and Loop bundle adds all POST with
  // `sections` and dispatch their own cart:update. Bridging those double-fires and races the Loop
  // bundle's multi-line add (which broke WOO add). So we only bridge a /cart/add that does NOT
  // carry `sections` — i.e. a genuine Rebuy add (Rebuy doesn't send a sections param).
  const originalFetch = window.fetch;
  function rebuyAddCarriesSections(options) {
    try {
      const b = options && options.body;
      if (!b) return false;
      if (typeof b === 'string') return b.indexOf('sections') !== -1;
      if (typeof FormData !== 'undefined' && b instanceof FormData) return b.has('sections');
      if (typeof URLSearchParams !== 'undefined' && b instanceof URLSearchParams) return b.has('sections');
    } catch (e) {}
    return false;
  }
  window.fetch = function(url, options) {
    const urlString = typeof url === 'string' ? url : url.toString();
    const isRebuyAdd = urlString.includes('/cart/add') && !rebuyAddCarriesSections(options);

    return originalFetch.apply(this, arguments).then(response => {
      if (isRebuyAdd && response.ok) {
        // Match main's proven flow: fetch /cart.js (with bundled sections) and emit ONE
        // cart:update — cart-items-component does the single re-render. No manual morph and
        // no inline-script re-run here: the Rebuy widget lives INSIDE cart-items-component,
        // and a second late morph + script re-execution kills its DOM/listeners (frozen
        // Rebuy buttons, blank drawer body on mobile).
        response.clone().json().then(() => {
          setTimeout(() => {
            const sectionId = document.querySelector('cart-items-component')?.dataset?.sectionId;
            const sectionsParam = sectionId ? `?sections=${sectionId}` : '';

            originalFetch('/cart.js' + sectionsParam, { cache: 'no-store' })
              .then(r => r.json())
              .then(cart => {
                // Guard: only pass sections whose HTML is really this section (a bot-challenge
                // or truncated payload would make the theme's morph throw and kill the cart UI).
                let sections = cart.sections;
                const html = sections && sectionId && sections[sectionId];
                if (typeof html !== 'string' || html.indexOf('cart-items-component') === -1) {
                  sections = undefined; // cart-items-component falls back to its own section re-render
                }

                document.dispatchEvent(new CustomEvent('cart:update', {
                  bubbles: true,
                  detail: {
                    resource: cart,
                    data: {
                      sections: sections,
                      itemCount: cart.item_count
                    }
                  }
                }));

                const dlg = document.querySelector('dialog.cart-drawer__dialog');
                if (dlg) dlg.classList.toggle('cart-drawer--empty', (cart.item_count || 0) === 0);

                // Scroll cart drawer to top after adding product
                setTimeout(() => {
                  const cartItems = document.querySelector('.cart-drawer__items');
                  if (cartItems) {
                    cartItems.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }, 150);
              })
              .catch((err) => {
                console.error('[RebuyCartBridge] cart refresh failed; keeping current cart DOM.', err);
              });
          }, 100);
        }).catch(() => {});
      }
      return response;
    });
  };

  // Remove Rebuy's default CSS to use our custom styles
  function removeRebuyCss() {
    document.querySelectorAll('link[href*="rebuyengine.com"]').forEach(el => el.remove());
    document.querySelectorAll('style[id*="rebuy"], style[id="smart-cart-style"]').forEach(el => el.remove());
  }
  
  removeRebuyCss();
  
  // Observe for dynamically added Rebuy stylesheets
  const cssObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.tagName === 'LINK' && node.href?.includes('rebuyengine.com')) {
          node.remove();
        }
        if (node.tagName === 'STYLE' && node.id && (node.id.includes('rebuy') || node.id === 'smart-cart-style')) {
          node.remove();
        }
      }
    }
  });
  cssObserver.observe(document.head, { childList: true });
})();
